import classNames from "classnames";
import { type ForwardedRef, type ReactNode, forwardRef } from "react";

import { type PolymorphicPropsWithRef } from "@/types";

export type TextProps = {
  color?: keyof typeof colors;
  size?: keyof typeof sizes;
  weight?: keyof typeof weights;
  children?: ReactNode;
};

const colors = {
  default: "",
  primary: "text-primary-700",
  gray: "text-gray-600",
  "gray-dark": "text-gray-700",
  light: "",
  success: "",
  failure: "text-error-500",
  warning: "",
  purple: "",
};

const sizes = {
  xs: "text-xs",
  sm: "text-sm",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
  "5xl": "text-5xl",
  "6xl": "text-6xl",
  "7xl": "text-7xl",
  "8xl": "text-8xl",
  base: "text-base",
};

const weights = {
  light: "font-light",
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

type TextComponent = <As extends React.ElementType = "span">(
  props: PolymorphicPropsWithRef<As, TextProps>,
) => React.ReactNode | null;

const Text: TextComponent = forwardRef(function Text<
  As extends React.ElementType = "span",
>(
  {
    color = "default",
    size = "base",
    weight,
    className,
    as,
    ...rest
  }: PolymorphicPropsWithRef<As, TextProps>,
  ref?: ForwardedRef<As>,
) {
  const Component = as || "span";
  return (
    <Component
      className={classNames(
        className,
        colors[color],
        size && sizes[size],
        weight && weights[weight],
      )}
      ref={ref}
      {...rest}
    />
  );
});

export default Text;
